import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const ItemMasterDataDialog = ({ 
  open, 
  onClose, 
  onSave, 
  data, 
  mode, 
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState('');
  
  const itemTypes = [
    { code: 10, name: t('pages.master.item.itemTypes.finished') },
    { code: 20, name: t('pages.master.item.itemTypes.semiFinished') },
    { code: 30, name: t('pages.master.item.itemTypes.rawMaterial') },
    { code: 40, name: t('pages.master.item.itemTypes.subsidiaryMaterial') },
  ];

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && data) {
        setFormData(data);
      } else {
        // 추가 모드일 때 빈 데이터로 초기화
        setFormData({
          item_code: '',
          item_name: '',
          item_type_code: 10,
          status: 1,
          price: 0,
          description: ''
        });
      }
      setErrors({});
      setAlertMessage('');
    }
  }, [open, data, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'price' || name === 'item_type_code' || name === 'status') {
        processedValue = value === '' ? '' : Number(value);
    }
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.item_code) newErrors.item_code = t('pages.master.item.dialog.validations.itemCodeRequired');
    if (!formData.item_name) newErrors.item_name = t('pages.master.item.dialog.validations.itemNameRequired');
    if (formData.price < 0 || formData.price === '') newErrors.price = t('pages.master.item.dialog.validations.priceInvalid');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData, mode);
    } else {
      setAlertMessage(t('pages.master.item.dialog.validations.fillRequiredFields'));
    }
  };

  const getDialogTitle = () => {
    return mode === 'add' ? t('pages.master.item.dialog.title.add') : t('pages.master.item.dialog.title.edit');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
        {getDialogTitle()}
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {alertMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {alertMessage}
          </Alert>
        )}
        
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="item_code"
                    label={t('pages.master.item.dialog.form.itemCode')}
                    fullWidth
                    value={formData.item_code || ''}
                    onChange={handleChange}
                    error={!!errors.item_code}
                    helperText={errors.item_code}
                    required
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="item_name"
                    label={t('pages.master.item.dialog.form.itemName')}
                    fullWidth
                    value={formData.item_name || ''}
                    onChange={handleChange}
                    error={!!errors.item_name}
                    helperText={errors.item_name}
                    required
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                    <InputLabel>{t('pages.master.item.dialog.form.itemType')}</InputLabel>
                    <Select
                        name="item_type_code"
                        value={formData.item_type_code !== undefined ? formData.item_type_code : ''}
                        onChange={handleChange}
                        label={t('pages.master.item.dialog.form.itemType')}
                    >
                        {itemTypes.map(type => (
                            <MenuItem key={type.code} value={type.code}>{type.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
             <Grid item xs={12} sm={6}>
                <TextField
                    name="price"
                    label={t('pages.master.item.dialog.form.price')}
                    type="number"
                    fullWidth
                    value={formData.price !== undefined ? formData.price : ''}
                    onChange={handleChange}
                    error={!!errors.price}
                    helperText={errors.price}
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="description"
                    label={t('pages.master.item.dialog.form.description')}
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.description || ''}
                    onChange={handleChange}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <InputLabel>{t('pages.master.item.dialog.form.status')}</InputLabel>
                    <Select
                        name="status"
                        value={formData.status !== undefined ? formData.status : 1}
                        onChange={handleChange}
                        label={t('pages.master.item.dialog.form.status')}
                    >
                        <MenuItem value={1}>{t('pages.master.item.dialog.status.active')}</MenuItem>
                        <MenuItem value={0}>{t('pages.master.item.dialog.status.deleted')}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('pages.master.item.dialog.buttons.save')}
        </Button>
        <Button onClick={onClose} color="inherit">
          {t('pages.master.item.dialog.buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemMasterDataDialog; 